/**
 * Allergens at the counter, on the line and on the menu (coverage audit gap A1.9, 2026-09-30).
 *
 * The moment: 12:15, lunch rush. A server asks the cook "Is there sesame in this?". The cook needs the answer in one
 * glance and, when it's a yes-we-can, a one-tap safe-prep list. The diner's good (G4) is the end: a wrong "no" can kill.
 *
 * Sources (read 2026-09-30; Utah adopts the 2022 Food Code in R392-100-4(1)(b)):
 * - Food Code 2022 1-201.10(B) "Major food allergen": "(a) Milk, EGG, FISH ..., crustacean shellfish ..., tree nuts
 *   ..., wheat, peanuts, soybeans and sesame" (sesame added by the FASTER Act, Public Law 117-11, from 1/1/2023).
 * - 3-602.12(C): "The PERMIT HOLDER shall notify CONSUMERS by written notification of the presence of MAJOR FOOD
 *   ALLERGENS as an ingredient in unPACKAGED FOOD items that are served or sold to the CONSUMER."
 * - 2-102.11(C)(9) (the person in charge's knowledge; it is (C)(9) in 2022, not (C)(8)): "Describing FOODS identified
 *   as MAJOR FOOD ALLERGENS and the symptoms that a MAJOR FOOD ALLERGEN could cause in a sensitive individual".
 * - 2-103.11(O): "EMPLOYEES are properly trained in FOOD safety, including FOOD allergy awareness, as it relates to
 *   their assigned duties." (The December 2024 Supplement Utah adopted adds (R), food defense; (O) is unchanged.)
 * - R392-103-6 (Utah food handler training; Cornell LII copy, amended 9/15/2020): approved courses must "Define and
 *   give examples of the major food allergens", "Describe the range of symptoms ... to anaphylactic shock or death",
 *   and "Identify steps to prevent cross-contact of food allergens, and stress that cooking does not remove an allergen".
 * - Cross-contact steps: 2-301.14(F),(H) (wash hands when changing tasks and before gloves), 3-304.15(A) (gloves for one
 *   task, discarded when interrupted), 4-602.11(A)(5) (clean food-contact surfaces "At any time ... when contamination
 *   may have occurred"), Annex 3 on 2-103.11(O) ("Principles of allergen cross-contact prevention"), and the house
 *   protocols (docs/source/*-protocol.md "Cross-contact": soap removes allergens, sanitizer doesn't; no shared fryer oil;
 *   "If you can't guarantee an allergen-free drink, say so. Never guess.").
 *
 * Truth rules (tested in allergens.test.ts):
 * | A1 Never "free" | no line here ever says "allergen-free" or "free of"; the most we say is "not an ingredient" |
 * | A2 Only confirmed | a dish's allergens are fact only after the owner confirms them for this place; until then it says "Not confirmed" and is never shown without allergens |
 * | A3 Cross-contact | every "not an ingredient" line carries "cross-contact is possible" |
 * | A4 Public is opt-in | the customer page is off until the owner turns it on (Location.allergenMenuPublic === true) |
 * | A5 Nine | the list is the 9 major allergens, sesame included |
 * | A6 Own dishes | a dish the owner photographed or typed (menuphoto.ts) is on the menu only once confirmed: it lives only in
 *   menuAllergens under an "own-" key, with its name; an unconfirmed photographed dish is never on any list here |
 */
import { MAJOR_ALLERGENS, allergenLabel } from "./incidents.ts";
import type { IsoDate, IsoDateTime } from "./model.ts";
import { MENU_ITEMS, type MenuItem } from "./playbooks.ts";
import { matchMenuItemByAlias } from "./setupscan.ts";
import { ALLERGENS_UT } from "./states/ut/allergens.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof ALLERGENS_UT>> = perStateTable("ALLERGENS", ALLERGENS_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

export const ALLERGEN_CITATION = "Food Code 1-201.10(B); 3-602.12(C)";
export const AWARENESS_CITATION = cite("Food Code 2-102.11(C)(9), 2-103.11(O)", words("UT").awarenessRule);

/** The 9 major allergen ids (A5), in the order Food Code 1-201.10(B) lists them. */
export const NINE: readonly string[] = MAJOR_ALLERGENS.map((a) => a.id);

/** What the owner confirmed for one dish at one place. `allergens` may be empty: confirmed "none of the nine as ingredients". */
export interface DishAllergenConfirmation {
  allergens: string[];
  confirmedAt: IsoDateTime;
  /** The confirmer's name, as the member list has it. */
  confirmedBy: string;
  /** Own dishes only (an "own-" key, menuphoto.ts): the dish's name and description as printed or typed, and its place on the menu. */
  name?: string;
  description?: string;
  order?: number;
  /** Own dishes only: where the dish came from. */
  from?: "menu_photo" | "by_hand";
}
/** Location.menuAllergens: menu item id → the owner's confirmation. Absent id = not confirmed. */
export type MenuAllergens = Record<string, DishAllergenConfirmation>;

export interface AllergenChip { id: string; label: string }

export interface DishAllergens {
  menuItemId: string;
  name: string;
  confirmed: boolean;
  /** Confirmed only: the allergens that are ingredients. Empty when not confirmed (A2): nothing is fact yet. */
  contains: string[];
  /** Not confirmed: what dishes like this often have (playbooks.ts), for the owner to start from. Never shown as fact. */
  often: string[];
  confirmedAt?: IsoDateTime;
  confirmedBy?: string;
  /** The chips to show: confirmed → what it contains; not confirmed → what it often has (shown under "Not confirmed"). */
  chips: AllergenChip[];
  /** One line for the cook: "Contains: Milk, Soy." / "Not confirmed. Often has: Milk. Ask the manager." */
  line: string;
}

export const NOT_CONFIRMED = "Not confirmed";
export const CROSS_CONTACT = "Cross-contact is possible.";
const chip = (id: string): AllergenChip => ({ id, label: shortLabel(id) });
/** "Shellfish" on a chip; the long label ("Shellfish (shrimp, crab, lobster)") is for the picker. */
export const shortLabel = (id: string) => allergenLabel(id).replace(/\s*\(.*\)$/, "");
const listOf = (ids: readonly string[]) => ids.map(shortLabel).join(", ");
/** Keeps only the nine, in their order, once each. */
export const cleanAllergens = (ids: readonly string[]) => NINE.filter((id) => ids.includes(id));

/** A dish's allergens at this place (A2): the owner's confirmation, or "Not confirmed". */
export function dishAllergens(loc: { menuAllergens?: MenuAllergens }, item: Pick<MenuItem, "id" | "name" | "allergens">): DishAllergens {
  const c = loc.menuAllergens?.[item.id];
  if (c) {
    const contains = cleanAllergens(c.allergens);
    return {
      menuItemId: item.id, name: item.name, confirmed: true, contains, often: [], confirmedAt: c.confirmedAt, confirmedBy: c.confirmedBy,
      chips: contains.map(chip),
      line: contains.length ? `Contains: ${listOf(contains)}.` : `None of the 9 major allergens is an ingredient. ${CROSS_CONTACT}`,
    };
  }
  const often = cleanAllergens(item.allergens);
  return {
    menuItemId: item.id, name: item.name, confirmed: false, contains: [], often,
    chips: often.map(chip),
    line: often.length ? `${NOT_CONFIRMED}. Often has: ${listOf(often)}. Ask the manager.` : `${NOT_CONFIRMED}. Ask the manager. Never guess.`,
  };
}

/** A dish the owner photographed or typed (menuphoto.ts) is kept under this prefix; no catalog id starts with it. */
export const OWN_DISH_PREFIX = "own-";
export const isOwnDish = (id: string) => id.startsWith(OWN_DISH_PREFIX);
/** FNV-1a 32-bit, 6 hex: a short, stable tag for a name (no crypto needed: it only keeps two long names apart). */
const tag6 = (s: string) => { let h = 0x811c9dc5; for (const ch of s) { h ^= ch.codePointAt(0)!; h = Math.imul(h, 0x01000193) >>> 0; } return h.toString(16).padStart(8, "0").slice(0, 6); };
/**
 * The key for an own dish: its name, folded ("Honey Lavender Latte" → "own-honey-lavender-latte"). The same name is the same dish.
 * NFKC first (fullwidth and ligature forms fold to their plain letters), then NFKD so an accent drops to its base letter ("Café" →
 * "cafe"). A name longer than the key, or one with no Latin letters at all ("抹茶ラテ"), gets a 6-hex tag of the NFKC name, so two
 * different long or non-Latin names never share a key (security review L2).
 */
export const MAX_OWN_KEY = 60;
export const ownDishKey = (name: string) => {
  const nfkc = name.normalize("NFKC").trim().toLowerCase();
  const folded = nfkc.normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (folded && folded.length <= MAX_OWN_KEY) return `${OWN_DISH_PREFIX}${folded}`;
  const head = (folded || "dish").slice(0, MAX_OWN_KEY - 7).replace(/-+$/g, "");
  return `${OWN_DISH_PREFIX}${head}-${tag6(nfkc)}`;
};
/** At most this many own dishes at one place (security review L4): a real menu is far smaller; a script can't grow the location row. */
export const MAX_OWN_DISHES = 300;
/** The refusal when a NEW own dish would pass MAX_OWN_DISHES; null when it fits (re-confirming an existing dish always fits). */
export function ownDishRoom(loc: { menuAllergens?: MenuAllergens }, key: string): string | null {
  if (!isOwnDish(key) || loc.menuAllergens?.[key]) return null;
  const n = Object.keys(loc.menuAllergens ?? {}).filter(isOwnDish).length;
  return n >= MAX_OWN_DISHES ? `This menu already has ${MAX_OWN_DISHES} of your own dishes. Take one off before adding another.` : null;
}

/** The owner's own confirmed dishes (A6), in menu order. */
export function ownDishes(loc: { menuAllergens?: MenuAllergens }): DishAllergens[] {
  return Object.entries(loc.menuAllergens ?? {})
    .filter(([id, c]) => isOwnDish(id) && typeof c?.name === "string" && c.name)
    .sort(([, a], [, b]) => (a.order ?? 1e9) - (b.order ?? 1e9) || a.name!.localeCompare(b.name!))
    .map(([id, c]) => dishAllergens(loc, { id, name: c.name!, allergens: [] }));
}

/**
 * Every dish on this place's menu, in menu order: the catalog dishes, then the owner's own (A6). A catalog dish that isn't confirmed
 * is left out once one of the owner's own dishes is that kind of dish ("Honey latte" covers "Lattes and steamed-milk drinks"): the
 * real dish says more than the category, and a guest never reads "Not confirmed" for a dish the menu doesn't name.
 */
export function menuAllergens(loc: { menuItemIds: string[]; menuAllergens?: MenuAllergens }): DishAllergens[] {
  const own = ownDishes(loc);
  const covered = new Set(own.map((d) => matchMenuItemByAlias(d.name)?.id).filter(Boolean));
  const catalog = MENU_ITEMS.filter((m) => loc.menuItemIds.includes(m.id)).map((m) => dishAllergens(loc, m)).filter((d) => d.confirmed || !covered.has(d.menuItemId));
  return [...catalog, ...own];
}

/** How many of this place's dishes the owner has confirmed. */
export function confirmedCount(loc: { menuItemIds: string[]; menuAllergens?: MenuAllergens }): { confirmed: number; total: number } {
  const all = menuAllergens(loc);
  return { confirmed: all.filter((d) => d.confirmed).length, total: all.length };
}

export interface AllergenAnswer { tone: "stop" | "go" | "warn"; line: string }

/**
 * "Is there sesame in this?" in one line (the 12:15 moment). A2/A3: yes only from a confirmation; "not an ingredient"
 * only from a confirmation and always with cross-contact; anything unconfirmed is "we can't be sure".
 */
export function allergenAnswer(d: DishAllergens, allergenId: string): AllergenAnswer {
  const name = shortLabel(allergenId).toLowerCase();
  if (!d.confirmed) return { tone: "warn", line: `${NOT_CONFIRMED}. Don't guess. Tell the guest we can't be sure about ${name}.` };
  if (d.contains.includes(allergenId)) return { tone: "stop", line: `Yes. It has ${name}. Don't make it for this guest.` };
  return { tone: "go", line: `No ${name} as an ingredient. ${CROSS_CONTACT}` };
}

/**
 * The allergy order's answer screen, the same on web and phone (T7). `d` null: a dish not on the menu list, which is
 * never confirmed. Title: "Don't make it" if any allergy is an ingredient; "We can't be sure" if any is unconfirmed.
 */
export function allergyOrderAnswer(d: DishAllergens | null, allergens: readonly string[]): { title: string; answers: (AllergenAnswer & { allergen: string })[]; stop: boolean; unsure: boolean } {
  const answers = allergens.map((a) => ({ allergen: a, ...(d ? allergenAnswer(d, a) : { tone: "warn" as const, line: `Not on the menu list. Don't guess. Tell the guest we can't be sure about ${shortLabel(a).toLowerCase()}.` }) }));
  const stop = answers.some((x) => x.tone === "stop");
  const unsure = answers.some((x) => x.tone === "warn");
  return { title: stop ? "Don't make it" : unsure ? "We can't be sure" : "You can make it safely", answers, stop, unsure };
}

/* ─────────────── the allergy order: one tap, a sourced step list, a record ─────────────── */

export interface AllergyStep {
  id: "wash" | "gloves" | "surface" | "utensils" | "fryer" | "label" | "tell";
  /** The step, ≤ 12 words (S15). */
  say: string;
  /** Why, one short line. */
  why: string;
  citation: string;
}

/** Does this dish use a fryer? From its own playbook steps (fries: "Don't share fryer oil ..."). */
export const usesFryer = (item?: Pick<MenuItem, "id" | "steps">) => !!item && item.steps.some((s) => /fryer|fried|fry /i.test(s.instruction));

/** The safe-prep steps for an allergy order. The fryer step only when the dish is fried. */
export function allergyOrderSteps(item?: Pick<MenuItem, "id" | "steps">): AllergyStep[] {
  const steps: AllergyStep[] = [
    { id: "wash", say: "Wash your hands with soap and water.", why: "Hands carry allergen protein. Sanitizer gel doesn't remove it.", citation: "Food Code 2-301.14(F), (H)" },
    { id: "gloves", say: "Put on new gloves.", why: "Old gloves carry the last food you touched.", citation: "Food Code 3-304.15(A); 2-301.14(H)" },
    { id: "surface", say: "Wash, rinse and sanitize the board and counter.", why: "Soap removes allergens. Sanitizer alone does not.", citation: "Food Code 4-602.11(A)(5); parish protocol: Cross-contact, step 5" },
    { id: "utensils", say: "Use clean utensils, pan and pitcher.", why: "One smear on tongs or a steam wand is enough.", citation: "Food Code 2022 Annex 3, 2-103.11(O): cross-contact prevention; coffee protocol: Cross-contact, steps 1-2" },
  ];
  if (usesFryer(item)) steps.push({ id: "fryer", say: "Fry in oil the allergen never touched.", why: "Shared fryer oil carries fish, shellfish and breading.", citation: "Parish protocol: Cross-contact, step 7; docs/source/research/menu-items.md" });
  steps.push(
    { id: "label", say: "Mark the ticket and plate: ALLERGY.", why: "Everyone who touches it knows.", citation: "Food Code 2022 Annex 3, 2-103.11(O): establishment allergen procedures and communication" },
    { id: "tell", say: "Tell the server: made for the allergy. Cross-contact possible.", why: "Never promise more than you know.", citation: "Coffee protocol: Cross-contact, steps 6-7; parish protocol: Say it right" },
  );
  return steps;
}

/** The log the allergy order writes (templates.ts "allergy_order"). */
export const ALLERGY_ORDER_TEMPLATE = "allergy_order";
/** Said and recorded when a step couldn't be done: the guest is told the truth instead. */
export const ALLERGY_CANT = "Couldn't do every step. Told the server we can't make it safely.";

/** The row for the allergy order log. `skipped`: the step that couldn't be done (then safe = "no" with the fix). */
export function allergyOrderValues(o: { dish: string; allergens: readonly string[]; steps: readonly AllergyStep[]; skipped?: AllergyStep["id"]; time: string; initials: string }): { values: Record<string, string>; correctiveAction?: string } {
  const at = o.skipped ? o.steps.findIndex((s) => s.id === o.skipped) : -1;
  const done = at >= 0 ? o.steps.slice(0, at) : o.skipped ? [] : o.steps;
  return {
    values: { time: o.time, dish: o.dish, allergy: listOf(cleanAllergens(o.allergens)) || "Not sure", steps: done.map((s) => s.id).join(", "), safe: o.skipped ? "no" : "yes", action: o.skipped ? ALLERGY_CANT : "", initials: o.initials },
    ...(o.skipped ? { correctiveAction: ALLERGY_CANT } : {}),
  };
}

/* ─────────────── the food label ─────────────── */

/**
 * The allergen line for a food label, when the label names a dish on this place's menu (strict alias match, so "Milk"
 * is never taken for "Lattes" and given soy and tree nuts). Undefined when the food isn't one of this place's dishes.
 */
export function labelAllergenLine(name: string, loc: { menuItemIds: string[]; menuAllergens?: MenuAllergens }): { line: string; dish: DishAllergens } | undefined {
  const m = matchMenuItemByAlias(name);
  if (!m || !loc.menuItemIds.includes(m.id)) return undefined;
  const d = dishAllergens(loc, m);
  if (!d.confirmed) return { line: `Allergens: ${NOT_CONFIRMED.toLowerCase()}`, dish: d };
  return { line: d.contains.length ? `Contains: ${listOf(d.contains)}` : "No major allergen ingredients", dish: d };
}

/* ─────────────── the customer's allergen menu (3-602.12(C)) ─────────────── */

export interface AllergenMenu {
  title: string;
  /** "Updated Tue 9/29" from the newest confirmation, or the day it was made when nothing is confirmed yet. */
  dated: string;
  items: { name: string; confirmed: boolean; line: string; chips: AllergenChip[] }[];
  ask: string;
  caveat: string;
  /** What "not listed" means, only for confirmed dishes. */
  notListed: string;
  citation: string;
}

export const MENU_ASK = "Have a food allergy? Ask your server before you order.";
export const MENU_CAVEAT = "We share kitchen equipment, so cross-contact is possible with any dish.";
export const MENU_NOT_LISTED = "An allergen not listed for a confirmed dish is not an ingredient. It may still be present from cross-contact.";

const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const shortDay = (d: IsoDate) => { const [y, m, dd] = d.split("-").map(Number); const dt = new Date(Date.UTC(y!, m! - 1, dd!)); return `${DAY[dt.getUTCDay()]} ${m}/${dd}`; };

/** The customer's allergen menu for one place, built only from confirmed data (A2). `localDay` turns an instant into the place's day. */
export function allergenMenu(loc: { name: string; menuItemIds: string[]; menuAllergens?: MenuAllergens }, o: { today: IsoDate; localDay: (iso: IsoDateTime) => IsoDate }): AllergenMenu {
  const dishes = menuAllergens(loc);
  const newest = dishes.map((d) => d.confirmedAt).filter((x): x is string => !!x).sort().at(-1);
  return {
    title: `${loc.name}: allergens in our menu`,
    dated: newest ? `Updated ${shortDay(o.localDay(newest))}` : `Printed ${shortDay(o.today)}`,
    items: dishes.map((d) => d.confirmed
      ? { name: d.name, confirmed: true, line: d.contains.length ? `Contains: ${listOf(d.contains)}.` : "None of the 9 major allergens is an ingredient.", chips: d.chips }
      : { name: d.name, confirmed: false, line: `${NOT_CONFIRMED} yet. Ask your server.`, chips: [] }),
    ask: MENU_ASK, caveat: MENU_CAVEAT, notListed: MENU_NOT_LISTED, citation: ALLERGEN_CITATION,
  };
}

/** Is the customer page on? Only an explicit true (A4): absent, null or false is off. */
export const allergenMenuPublic = (loc: { allergenMenuPublic?: boolean | null }) => loc.allergenMenuPublic === true;

/** Where the public page lives, on the web app (App.tsx) and behind it the API (GET /public/allergens/:orgId/:locationId). */
export const allergenMenuPath = (orgId: string, locationId: string) => `/allergen-menu/${encodeURIComponent(orgId)}/${encodeURIComponent(locationId)}`;

/** Checks an owner's confirmation body: only the nine ids. Returns the clean list, or an error line. */
export function parseAllergenPicks(raw: unknown): { allergens: string[] } | { error: string } {
  if (!Array.isArray(raw) || raw.some((x) => typeof x !== "string")) return { error: '"allergens" must be a list of allergen names.' };
  const bad = (raw as string[]).find((x) => !NINE.includes(x));
  if (bad !== undefined) return { error: `Unknown allergen "${bad}". Pick from: ${NINE.join(", ")}.` };
  return { allergens: cleanAllergens(raw as string[]) };
}

/**
 * The Next card's quiet "Allergy order" link (under its one primary button, next.ts N4): offered whenever this person
 * may do food work and the place has a menu. Never on a stop-work or a gate: no food work means no allergy prep.
 */
export function allergyOrderOffered(next: { kind: string }, menuItemIds: readonly string[]): boolean {
  return next.kind !== "stop_work" && next.kind !== "gate" && menuItemIds.length > 0;
}
